import { useState, useEffect, useCallback, useLayoutEffect, type RefObject } from 'react';
import { format, formatISO } from 'date-fns';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { isValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { DateOnly } from '@atlassian/jira-platform-utils-date-only/src/index.tsx';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { importForInteraction, waitForAllResources } from '@atlassian/react-async';
import {
	SCHEDULE_ITEM_EXPERIENCE,
	DRAGGING_UNSCHEDULED_ITEM_CLASS_NAME,
} from '../../common/constants.tsx';
import { NotFoundError, type UpdateDatesProps } from '../../services/update-dates/index.tsx';
import type { UnscheduledItem, Refetch } from '../../types.tsx';
import { useDecrementUnscheduledItemsCount } from '../unscheduled-items-count/index.tsx';
import { useUpdateDates } from '../update-dates/index.tsx';

type Props = {
	setPlaceholder: (event: unknown) => void;
	setIsSaving: (saving: boolean) => void;
	refetch: Refetch;
	calendarWrapperRef: RefObject<HTMLElement>;
};

type HighlightDaysParams = {
	startDate: DateOnly;
	dueDate: DateOnly;
};

const CELL_ID_PREFIX = 'cell-date';
const getElementAdapter = () =>
	importForInteraction(
		() =>
			import(
				/* webpackChunkName: "async-drag-and-drop-element" */ '@atlaskit/pragmatic-drag-and-drop/element/adapter'
			),
	);

const addDraggingClassToCalendar = (calendarElement: HTMLDivElement) => {
	calendarElement.classList.add(DRAGGING_UNSCHEDULED_ITEM_CLASS_NAME);
};

const removeDraggingClassFromCalendar = (calendarElement: HTMLDivElement) => {
	calendarElement.classList.remove(DRAGGING_UNSCHEDULED_ITEM_CLASS_NAME);
};

export const useDragUnscheduledItem = ({
	setPlaceholder,
	setIsSaving,
	refetch,
	calendarWrapperRef,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const startExperience = useExperienceStart(SCHEDULE_ITEM_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(SCHEDULE_ITEM_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(SCHEDULE_ITEM_EXPERIENCE);

	const decrementCount = useDecrementUnscheduledItemsCount();

	const updateDates = useUpdateDates();

	const [updateIssueParams, setUpdateIssueParams] = useState<UpdateDatesProps | null>(null);

	const cleanHighlightDays = useCallback(() => {
		if (typeof document !== 'undefined') {
			const elements = document.getElementsByClassName('fc-daygrid-day-frame');
			Array.from(elements).forEach((element) => element.classList.remove('fc-highlight'));
		}
	}, []);

	const highlightDays = useCallback((params: HighlightDaysParams) => {
		if (typeof document !== 'undefined') {
			const elements = document.getElementsByClassName('fc-daygrid-day-frame');
			Array.from(elements).forEach((element) => {
				const elementDate = element.id.split('_')[1];
				const elementDateMapped = DateOnly.fromDateAccordingToLocalTime(new Date(elementDate));

				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const { startDate, dueDate } = params as HighlightDaysParams;
				if (
					DateOnly.compare(startDate, elementDateMapped) === 0 ||
					DateOnly.compare(dueDate, elementDateMapped) === 0 ||
					(DateOnly.compare(startDate, elementDateMapped) === -1 &&
						DateOnly.compare(dueDate, elementDateMapped) === 1) ||
					(DateOnly.compare(startDate, elementDateMapped) === 1 &&
						DateOnly.compare(dueDate, elementDateMapped) === -1)
				) {
					element.classList.add('fc-highlight');
				} else {
					element.classList.remove('fc-highlight');
				}
			});
		}
	}, []);

	// This function give us access to the calendar dates cell to be able to convert them as a drop element.
	// This allow us to highlight the element once the element is dragged over the date.
	const dayCellDidMount = useCallback(
		({ el, date }: { el?: HTMLElement; date: Date }) => {
			if (!el) {
				return;
			}

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const firstChild = el.firstChild as HTMLDivElement;
			if (firstChild) {
				const mappedDate = format(date, 'yyyy-MM-dd');

				firstChild.id = `${CELL_ID_PREFIX}_${mappedDate}`;
			}

			getElementAdapter().onComplete(({ dropTargetForElements }) =>
				dropTargetForElements({
					element: el,
					getData: () => ({ date }),
					onDragEnter: ({ self, source }) => {
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						const selfDate = self.data.date as Date;
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						const issue = source.data.issue as UnscheduledItem;

						if (issue.startdate) {
							// If the ticket has a start date we want to highlight it from the start date until the hovered date,
							// and that is what is does the method highlightDays
							highlightDays({
								startDate: issue.startdate,
								dueDate: DateOnly.fromDateAccordingToLocalTime(selfDate),
							});
						} else {
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							const element = self.element as HTMLElement;
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							const firstDiv = element.firstChild as HTMLDivElement;
							if (firstDiv) {
								firstDiv.classList.add('fc-highlight');
							}
						}
					},
					onDragLeave: ({ self, source }) => {
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						const element = self.element as HTMLElement;
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						const issue = source.data.issue as UnscheduledItem;

						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						const firstDiv = element.firstChild as HTMLDivElement;
						if (!issue.startdate && firstDiv) {
							firstDiv.classList.remove('fc-highlight');
						}
					},
					onDrop: ({ self, source }) => {
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						const selfDate = self.data.date as Date;
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						const issue = source.data.issue as UnscheduledItem;

						const mappedNewDate = DateOnly.fromDateAccordingToLocalTime(selfDate);
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						const element = self.element as HTMLElement;
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						const firstDiv = element.firstChild as HTMLDivElement;

						if (firstDiv && !issue.startdate) {
							firstDiv.classList.remove('fc-highlight');
						} else {
							cleanHighlightDays();
						}

						// setPlaceholder adds an optimistic calendar item
						setPlaceholder({
							title: issue.key,
							extendedProps: {
								issue: {
									...issue,
									...(!issue.startdate ? { startdate: mappedNewDate } : {}),
									duedate: mappedNewDate,
								},
							},
							allDay: true,
							start: mappedNewDate.toLocalDate(),
							end: mappedNewDate.toLocalDate(),
						});
						setIsSaving(true);

						const newDueDate = formatISO(date, { representation: 'date' });
						fireUIAnalytics(createAnalyticsEvent({}), 'unscheduledItem dropped', {
							newDueDate,
						});

						setUpdateIssueParams({
							issueKey: issue.key,
							issueId: issue.id,
							...(!issue.startdate
								? { newStartDate: formatISO(date, { representation: 'date' }) }
								: {}),
							newDueDate,
						});
					},
				}),
			);
		},
		[highlightDays, setPlaceholder, setIsSaving, createAnalyticsEvent, cleanHighlightDays],
	);

	useLayoutEffect(() => {
		if (calendarWrapperRef?.current) {
			// This drop target was added to the whole calendar to be able to detect when
			// the card is dragged out from the calendar and remove the highlight from the cells
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const element = calendarWrapperRef.current as HTMLElement;
			getElementAdapter().onComplete(({ dropTargetForElements }) => {
				dropTargetForElements({
					element,
					onDragEnter: () => {
						addDraggingClassToCalendar(
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							calendarWrapperRef.current?.firstChild as HTMLDivElement,
						);
					},
					onDragLeave: () => {
						cleanHighlightDays();

						removeDraggingClassFromCalendar(
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							calendarWrapperRef.current?.firstChild as HTMLDivElement,
						);
					},
					onDrop: () => {
						removeDraggingClassFromCalendar(
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							calendarWrapperRef.current?.firstChild as HTMLDivElement,
						);
					},
				});
			});
		}
	}, [calendarWrapperRef, cleanHighlightDays]);

	useEffect(() => {
		if (updateIssueParams) {
			// Once we want to update dates `onDrop` we need to refetch the calendarItems
			// and get the start date field id to be able to call correctly the function `updateDates`.
			// We are not able to do it in the `onDrop` function because
			// `dayCellDidMount` could be called before refetch or issue types and fields are defined
			startExperience();

			(async () => {
				try {
					await updateDates(updateIssueParams);
					await refetch(undefined);
					setPlaceholder(null);
					decrementCount();
					fireTrackAnalytics(createAnalyticsEvent({}), 'scheduleItem success', {
						...updateIssueParams,
					});
					markExperienceSuccess();
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireTrackAnalytics(createAnalyticsEvent({}), 'scheduleItem failed', {
						error,
						...updateIssueParams,
					});

					if (error instanceof NotFoundError || isValidationError(error)) {
						markExperienceSuccess();
					} else {
						markExperienceFailed('schedule an item has failed', error);
					}

					setPlaceholder(null);
				} finally {
					setUpdateIssueParams(null);
					setIsSaving(false);
				}
			})();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateIssueParams]);

	useEffect(() => {
		waitForAllResources([
			importForInteraction(
				() =>
					import(
						/* webpackChunkName: "async-drag-and-drop-combine" */ '@atlaskit/pragmatic-drag-and-drop/combine'
					),
			),
			getElementAdapter(),
			importForInteraction(
				() =>
					import(
						/* webpackChunkName: "async-drag-and-drop-react-beautiful-dnd-autoscroll" */ '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-autoscroll'
					),
			),
		]).onComplete(([{ combine }, { monitorForElements }, { autoScroller }]) => {
			combine(
				monitorForElements({
					onDragStart: ({ location }) => {
						autoScroller.start({ input: location.current.input });
					},
					onDrop: () => {
						autoScroller.stop();
					},
					onDrag: ({ location }) => {
						autoScroller.updateInput({
							input: location.current.input,
						});
					},
				}),
				() => autoScroller.stop(),
			);
		});
	}, []);

	return { dayCellDidMount };
};
